import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Check, ImageIcon, Loader2, PenLine, RefreshCcw, Upload, X } from "lucide-react";
import { PhotoAnnotator, renderAnnotated } from "./PhotoAnnotator";

export const BurstCamera = ({ open, zh, onClose, onSubmit, onFallback }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shots, setShots] = useState([]);
  const [facing, setFacing] = useState("environment");
  const [err, setErr] = useState(null);
  const [starting, setStarting] = useState(false);
  const [flash, setFlash] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const stop = useCallback(() => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    streamRef.current = null;
  }, []);

  const start = useCallback(async (mode) => {
    stop();
    setErr(null);
    setStarting(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("unsupported");
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play().catch(() => {}); }
    } catch (e) { setErr(e?.name === "NotAllowedError" ? (zh ? "相机权限被拒绝" : "Camera denied") : (zh ? "无法打开相机" : "Camera unavailable")); }
    setStarting(false);
  }, [stop, zh]);

  useEffect(() => {
    if (open) start(facing);
    else { stop(); setShots((s) => { s.forEach((x) => URL.revokeObjectURL(x.url)); return []; }); }
    return stop;
  }, [open, facing, start, stop]);

  if (!open) return null;

  const shoot = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setFlash(true); setTimeout(() => setFlash(false), 120);
    c.toBlob((blob) => {
      if (!blob) return;
      const n = Date.now();
      const file = new File([blob], `photo_${new Date(n).toISOString().slice(0, 19).replace(/[-:T]/g, "")}_${shots.length + 1}.jpg`, { type: "image/jpeg" });
      setShots((s) => [...s, { key: n, file, url: URL.createObjectURL(blob), shapes: [], caption: "" }]);
    }, "image/jpeg", 0.9);
  };
  const remove = (k) => setShots((s) => { const x = s.find((i) => i.key === k); x && URL.revokeObjectURL(x.url); return s.filter((i) => i.key !== k); });
  const submit = async () => {
    setSubmitting(true);
    const files = await Promise.all(shots.map((s) => (s.shapes.length || s.caption ? renderAnnotated(s) : Promise.resolve(s.file))));
    setSubmitting(false);
    stop(); onSubmit(files);
  };
  const editing = shots.find((s) => s.key === editKey);

  return (
    <div className="fixed inset-0 z-[10001] bg-black flex flex-col" data-testid="burst-camera">
      <div className="mobile-safe-topbar flex items-center justify-between px-4 py-2.5 text-white/90">
        <p className="text-xs font-semibold flex items-center gap-1.5"><Camera className="h-4 w-4" />{zh ? "连拍上传证据" : "Burst capture"}
          <span className="text-[10px] text-white/50 font-normal">{zh ? "· 连续拍摄，最后一次提交" : "· shoot many, submit once"}</span></p>
        <div className="flex items-center gap-2">
          <button data-testid="burst-camera-flip" onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20" title={zh ? "切换前后摄像头" : "Flip"}><RefreshCcw className="h-4 w-4" /></button>
          <button data-testid="burst-camera-close" onClick={() => { stop(); onClose(); }} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="relative flex-1 min-h-0 flex items-center justify-center bg-black">
        <video ref={videoRef} playsInline muted autoPlay className="max-h-full max-w-full object-contain" data-testid="burst-camera-video" />
        {flash && <div className="absolute inset-0 bg-white/80 pointer-events-none" />}
        {starting && <div className="absolute inset-0 flex items-center justify-center text-white/70 text-xs gap-2"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "正在打开相机…" : "Starting camera…"}</div>}
        {err && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/80 text-sm px-6 text-center" data-testid="burst-camera-error">
            <p>{err}</p>
            <button data-testid="burst-camera-fallback" onClick={() => { stop(); onFallback(); }} className="text-xs px-3 py-2 rounded-lg bg-white text-slate-900 font-semibold">{zh ? "改用系统相机拍一张" : "Use system camera"}</button>
          </div>
        )}
        {shots.length > 0 && <span data-testid="burst-camera-count" className="absolute top-3 left-3 text-[11px] font-bold px-2 py-1 rounded-full bg-brand-500 text-white">{shots.length} {zh ? "张" : ""}</span>}
      </div>
      {shots.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto px-3 py-2 bg-black/90 items-center" data-testid="burst-camera-strip">
          {shots.map((s, i) => (
            <div key={s.key} className="relative shrink-0">
              <button data-testid={`burst-camera-edit-${i}`} onClick={() => setEditKey(s.key)} title={zh ? "标注此照片" : "Annotate"} className="block">
                <img src={s.url} alt="" className={`h-14 w-14 object-cover rounded-md border ${s.shapes.length || s.caption ? "border-emerald-400" : "border-white/20"}`} data-testid={`burst-camera-shot-${i}`} />
                {(s.shapes.length > 0 || s.caption) && <span data-testid={`burst-camera-marked-${i}`} className="absolute bottom-0.5 left-0.5 h-4 px-1 rounded bg-emerald-500 text-white text-[9px] font-bold inline-flex items-center gap-0.5"><PenLine className="h-2.5 w-2.5" />{s.shapes.length || "备"}</span>}
              </button>
              <button data-testid={`burst-camera-remove-${i}`} onClick={() => remove(s.key)} className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white text-red-600 flex items-center justify-center shadow"><X className="h-2.5 w-2.5" /></button>
            </div>
          ))}
          <span className="text-[10px] text-white/40 shrink-0 pl-1">{zh ? "点缩略图可圈画/写备注" : "Tap a shot to annotate"}</span>
        </div>
      )}
      <div className="flex items-center justify-between px-6 sm:px-16 py-4 bg-black">
        <span className="text-[11px] text-white/50 w-28 inline-flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" />{zh ? `已拍 ${shots.length} 张` : `${shots.length} shots`}</span>
        <button data-testid="burst-camera-shutter" onClick={shoot} disabled={!!err || starting}
          className="h-16 w-16 rounded-full bg-white border-4 border-white/40 ring-4 ring-white/20 active:scale-95 transition-transform disabled:opacity-30" title={zh ? "拍照" : "Shoot"} />
        <button data-testid="burst-camera-submit" onClick={submit} disabled={!shots.length || submitting}
          className={`w-28 text-[12px] font-semibold inline-flex items-center justify-center gap-1 rounded-full px-3 py-2.5 transition-colors ${shots.length ? "bg-emerald-500 text-white hover:bg-emerald-400" : "bg-white/10 text-white/40"}`}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : shots.length ? <Upload className="h-4 w-4" /> : <Check className="h-4 w-4" />}{submitting ? (zh ? "合成标注…" : "Rendering…") : zh ? `提交${shots.length ? ` ${shots.length} 张` : ""}` : `Submit${shots.length ? ` ${shots.length}` : ""}`}</button>
      </div>
      {editing && (
        <PhotoAnnotator shot={editing} zh={zh} onClose={() => setEditKey(null)}
          onSave={(shapes, caption, lang) => { setShots((s) => s.map((x) => (x.key === editing.key ? { ...x, shapes, caption, lang } : x))); setEditKey(null); }} />
      )}
    </div>
  );
};
